import { useState } from 'react';

import { Share07Icon } from '@/components/icons/Share07Icon';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useReportError } from '@/hooks/useReportError';

import {
  Operations,
  usePublishPresentationTemplateMutation,
  useUnpublishPresentationTemplateMutation,
} from './graphql/TenantPresentationTemplateTable.generated';

interface PresentationTemplatePublishingMenuItemProps {
  action: 'publish' | 'unpublish';
  presentationTemplateId: string;
}

export function PresentationTemplatePublishingMenuItem({
  action,
  presentationTemplateId,
}: PresentationTemplatePublishingMenuItemProps) {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const mutationOptions = {
    refetchQueries: [Operations.Query.TenantPresentationTemplateTable],
    awaitRefetchQueries: true,
  };
  const [publish, { loading: publishLoading }] =
    usePublishPresentationTemplateMutation(mutationOptions);
  const [unpublish, { loading: unpublishLoading }] =
    useUnpublishPresentationTemplateMutation(mutationOptions);
  const loading = isSubmitting || publishLoading || unpublishLoading;
  const actionLabel = action === 'unpublish' ? 'Unpublish' : 'Publish';

  const handleConfirm = async () => {
    if (loading) return;

    setIsSubmitting(true);
    try {
      const mutation = action === 'unpublish' ? unpublish : publish;
      await mutation({ variables: { id: presentationTemplateId } });
      showFeedback(
        action === 'unpublish'
          ? 'Presentation template unpublished successfully'
          : 'Presentation template published successfully',
        { variant: 'success' }
      );
      setIsConfirmationOpen(false);
    } catch (error) {
      const publishError =
        error instanceof Error ? error : new Error(String(error));
      showFeedback(
        `We weren't able to ${action} this presentation template. Please try again.`,
        { variant: 'error' }
      );
      reportError(`Failed to ${action} presentation template`, publishError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isConfirmationOpen && (
        <ConfirmationModal
          isOpen
          heading={`${actionLabel} presentation template?`}
          confirmText={actionLabel}
          closeOnOutsideClick={!loading}
          // This modal is rendered from a table action cell. Stop its button
          // clicks from reaching the row's navigation handler.
          confirmButtonProps={{
            loading,
            disabled: loading,
            onClick: (event) => {
              event.stopPropagation();
              void handleConfirm();
            },
          }}
          cancelButtonProps={{
            disabled: loading,
            onClick: (event) => {
              event.stopPropagation();
              setIsConfirmationOpen(false);
            },
          }}
          dialogContentProps={{ onClick: (event) => event.stopPropagation() }}
          onClose={() => {
            if (loading) return;
            setIsConfirmationOpen(false);
          }}
        >
          {action === 'unpublish'
            ? 'This template will be hidden from members. Existing presentations will not change.'
            : 'This template will be shared with your organization. Future edits will be live immediately.'}
        </ConfirmationModal>
      )}
      <MenuItem
        icon={<Share07Icon size={18} />}
        label={actionLabel}
        loading={loading}
        onClick={(event) => {
          event.stopPropagation();
          if (!loading) setIsConfirmationOpen(true);
        }}
      />
    </>
  );
}
